import React from "react";
import { formatCurrency, ELECTION_TYPES } from "../../lib/costEngine";
import { fmtDate } from "../../lib/proposalPdf";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Inbox, Loader2, Search } from "lucide-react";

export function ProposalListPanel({
  loading,
  proposals,
  filtered,
  query,
  setQuery,
  typeFilter,
  setTypeFilter,
  selectedId,
  onSelect,
}) {
  return (
    <aside className="lg:col-span-5 space-y-3" data-testid="proposals-list">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="search-input"
            placeholder="Search name, email, org, event…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="type-filter" className="sm:w-44">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="filter-all">All types</SelectItem>
            {Object.entries(ELECTION_TYPES).map(([key, val]) => (
              <SelectItem key={key} value={key} data-testid={`filter-${key}`}>
                {val.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground p-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : proposals.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            <Inbox className="h-8 w-8 mx-auto mb-3 opacity-60" />
            No proposals captured yet.
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground" data-testid="no-results">
            <Search className="h-8 w-8 mx-auto mb-3 opacity-60" />
            No leads match your search.
          </CardContent>
        </Card>
      ) : (
        filtered.map((p) => (
          <button
            key={p.id}
            data-testid={`proposal-card-${p.id}`}
            onClick={() => onSelect(p)}
            className={`w-full text-left rounded-xl border p-4 transition-colors ${
              selectedId === p.id
                ? "border-primary bg-accent/50"
                : "border-border bg-white hover:border-primary/40"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display font-600 text-foreground">{p.lead?.name || "Unknown"}</p>
                <p className="text-sm text-muted-foreground">{p.lead?.organization || "—"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{p.lead?.email || "—"}</p>
              </div>
              <div className="text-right">
                <p className="font-display font-700 text-primary tabular-nums">
                  {formatCurrency(p.total)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{fmtDate(p.createdAt)}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {ELECTION_TYPES[p.electionType]?.label || p.electionType}
              </Badge>
              <span className="text-xs text-muted-foreground truncate">{p.eventTitle}</span>
            </div>
          </button>
        ))
      )}
    </aside>
  );
}
